import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Waves, Bird, CloudRain, Circle, Flame, TreePine, Music, Volume2, VolumeX, Loader2, Droplets, Upload, Plus, Trash2, Timer, Moon, AlarmClock, X } from 'lucide-react';
import { useZen } from '../contexts/ZenContext';
import useKeepAwake from '../hooks/useKeepAwake';
import KeepAwakeToggle from './KeepAwakeToggle';
import { Slider } from './ui/slider';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  'waves': Waves,
  'bird': Bird,
  'cloud-rain': CloudRain,
  'circle': Circle,
  'flame': Flame,
  'tree-pine': TreePine,
  'music': Music,
  'droplets': Droplets
};

const ICON_OPTIONS = [
  { value: 'waves', label: 'Vagues', Icon: Waves },
  { value: 'bird', label: 'Oiseau', Icon: Bird },
  { value: 'cloud-rain', label: 'Pluie', Icon: CloudRain },
  { value: 'circle', label: 'Bol', Icon: Circle },
  { value: 'tree-pine', label: 'Forêt', Icon: TreePine },
  { value: 'droplets', label: 'Eau', Icon: Droplets },
  { value: 'music', label: 'Musique', Icon: Music },
];

export const AmbientMixer = () => {
  const [ambientSounds, setAmbientSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: '', icon: 'music' });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Sleep/Nap Timer
  const [showTimerPanel, setShowTimerPanel] = useState(false);
  const [timerMode, setTimerMode] = useState(null); // 'duration' | 'alarm' | null (unlimited)
  const [timerDuration, setTimerDuration] = useState(30); // minutes
  const [alarmTime, setAlarmTime] = useState('');
  const [timerActive, setTimerActive] = useState(false);
  const [keepAwake, toggleKeepAwake] = useKeepAwake('sieste', timerActive);
  const [timerRemaining, setTimerRemaining] = useState(0); // seconds
  const timerRef = useRef(null);
  
  // Alarm sound selection
  const [selectedAlarm, setSelectedAlarm] = useState(() => localStorage.getItem('alarm_sound') || '/api/uploads/alarms/zen_cloche.wav');
  const [showAlarmSounds, setShowAlarmSounds] = useState(false);
  const [customAlarms, setCustomAlarms] = useState(() => {
    try { return JSON.parse(localStorage.getItem('custom_alarms') || '[]'); } catch { return []; }
  });
  const [previewAudio, setPreviewAudio] = useState(null);
  const alarmFileRef = useRef(null);

  const {
    activeAmbientSounds,
    ambientVolumes,
    toggleAmbientSound,
    updateAmbientVolume,
    stopAllAmbient,
    masterVolume,
    updateMasterVolume
  } = useZen();

  useEffect(() => {
    fetchAmbientSounds();
  }, []);

  // Timer countdown
  useEffect(() => {
    if (!timerActive || timerRemaining <= 0) return;
    timerRef.current = setInterval(() => {
      setTimerRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setTimerActive(false);
          // Fade out and stop sounds
          stopAllAmbient();
          toast.success('Minuterie terminée — Sons arrêtés');
          // Play gentle alarm
          playAlarmSound();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [timerActive, stopAllAmbient]);

  const playAlarmSound = () => {
    try {
      const audio = new Audio(selectedAlarm.startsWith('http') ? selectedAlarm : `${API.replace('/api', '')}${selectedAlarm}`);
      audio.volume = 0.6;
      audio.play();
      // Repeat 2 times
      let count = 0;
      audio.onended = () => { count++; if (count < 2) audio.play(); };
    } catch (e) { /* fallback silent */ }
  };

  const previewAlarmSound = (url) => {
    if (previewAudio) { previewAudio.pause(); setPreviewAudio(null); }
    const audio = new Audio(url.startsWith('http') ? url : `${API.replace('/api', '')}${url}`);
    audio.volume = 0.5;
    audio.play();
    setPreviewAudio(audio);
    audio.onended = () => setPreviewAudio(null);
  };

  const selectAlarmSound = (url) => {
    setSelectedAlarm(url);
    localStorage.setItem('alarm_sound', url);
    previewAlarmSound(url);
  };

  const handleAlarmUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
    try {
      const res = await axios.post(`${API}/alarm-sound/upload`, formData);
      const newAlarm = { url: res.data.url, name: res.data.name };
      const updated = [...customAlarms, newAlarm];
      setCustomAlarms(updated);
      localStorage.setItem('custom_alarms', JSON.stringify(updated));
      selectAlarmSound(res.data.url);
      toast.success(`Son "${res.data.name}" ajouté`);
    } catch (err) {
      toast.error("Erreur lors de l'upload");
    }
    if (alarmFileRef.current) alarmFileRef.current.value = '';
  };

  const ALARM_CATEGORIES = [
    { id: 'nature', label: 'Nature', sounds: [
      { name: 'Oiseaux', url: '/api/uploads/alarms/nature_oiseaux.wav' },
      { name: 'Gouttes', url: '/api/uploads/alarms/nature_gouttes.wav' },
      { name: 'Carillon', url: '/api/uploads/alarms/nature_carillon.wav' },
      { name: 'Vagues', url: '/api/uploads/alarms/nature_vagues.wav' },
    ]},
    { id: 'doux', label: 'Doux', sounds: [
      { name: 'Berceuse', url: '/api/uploads/alarms/doux_berceuse.wav' },
      { name: 'Harpe', url: '/api/uploads/alarms/doux_harpe.wav' },
      { name: 'Clochettes', url: '/api/uploads/alarms/doux_clochettes.wav' },
      { name: 'Piano', url: '/api/uploads/alarms/doux_piano.wav' },
    ]},
    { id: 'energisant', label: 'Énergisant', sounds: [
      { name: 'Fanfare', url: '/api/uploads/alarms/energisant_fanfare.wav' },
      { name: 'Bip', url: '/api/uploads/alarms/energisant_bip.wav' },
      { name: 'Accords', url: '/api/uploads/alarms/energisant_accords.wav' },
      { name: 'Trompette', url: '/api/uploads/alarms/energisant_trompette.wav' },
    ]},
    { id: 'dynamique', label: 'Dynamique', sounds: [
      { name: 'Rythme', url: '/api/uploads/alarms/dynamique_rythme.wav' },
      { name: 'Montée', url: '/api/uploads/alarms/dynamique_montee.wav' },
      { name: 'Pulse', url: '/api/uploads/alarms/dynamique_pulse.wav' },
      { name: 'Claps', url: '/api/uploads/alarms/dynamique_claps.wav' },
    ]},
    { id: 'zen', label: 'Zen', sounds: [
      { name: 'Bol tibétain', url: '/api/uploads/alarms/zen_bol_tibetain.wav' },
      { name: 'Om', url: '/api/uploads/alarms/zen_om.wav' },
      { name: 'Cloche', url: '/api/uploads/alarms/zen_cloche.wav' },
      { name: 'Cristal', url: '/api/uploads/alarms/zen_cristal.wav' },
    ]},
    { id: 'monde', label: 'Musique du monde', sounds: [
      { name: 'Zouk', url: '/api/uploads/alarms/monde_zouk.wav' },
      { name: 'Djembé', url: '/api/uploads/alarms/monde_djembe.wav' },
      { name: 'Flûte', url: '/api/uploads/alarms/monde_flute.wav' },
      { name: 'Sitar', url: '/api/uploads/alarms/monde_sitar.wav' },
    ]},
  ];

  const startDurationTimer = (minutes) => {
    setTimerRemaining(minutes * 60);
    setTimerActive(true);
    setTimerMode('duration');
    setShowTimerPanel(false);
    toast.success(`Minuterie: ${minutes} min`);
  };

  const startAlarmTimer = () => {
    if (!alarmTime) { toast.error('Choisissez une heure de réveil'); return; }
    const [h, m] = alarmTime.split(':').map(Number);
    const now = new Date();
    const target = new Date(now);
    target.setHours(h, m, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);
    const diffSec = Math.floor((target - now) / 1000);
    setTimerRemaining(diffSec);
    setTimerActive(true);
    setTimerMode('alarm');
    setShowTimerPanel(false);
    const hours = Math.floor(diffSec / 3600);
    const mins = Math.floor((diffSec % 3600) / 60);
    toast.success(`Réveil à ${alarmTime} (dans ${hours}h${mins > 0 ? mins + 'min' : ''})`);
  };

  const cancelTimer = () => {
    clearInterval(timerRef.current);
    setTimerActive(false);
    setTimerRemaining(0);
    setTimerMode(null);
    toast('Minuterie annulée');
  };

  const formatRemaining = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) return `${h}h${m.toString().padStart(2, '0')}m`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const fetchAmbientSounds = async () => {
    try {
      const response = await axios.get(`${API}/ambient-sounds`);
      setAmbientSounds(response.data);
    } catch (error) {
      console.error('Error fetching ambient sounds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!uploadForm.name) {
        setUploadForm(prev => ({ ...prev, name: file.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.name) {
      toast.error('Sélectionnez un fichier et entrez un nom');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('name', uploadForm.name);
    formData.append('icon', uploadForm.icon);
    formData.append('category', 'custom');

    try {
      await axios.post(`${API}/ambient-sounds/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Son "${uploadForm.name}" ajouté !`);
      setSelectedFile(null);
      setUploadForm({ name: '', icon: 'music' });
      setShowUpload(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchAmbientSounds();
    } catch (error) {
      console.error('Error uploading sound:', error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (sound, e) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer "${sound.name}" ?`)) return;
    
    try {
      await axios.delete(`${API}/ambient-sounds/${sound.id}`);
      toast.success(`"${sound.name}" supprimé`);
      // Stop the sound if it's playing
      if (activeAmbientSounds[sound.id]) {
        toggleAmbientSound(sound);
      }
      fetchAmbientSounds();
    } catch (error) {
      console.error('Error deleting sound:', error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || Music;
    return IconComponent;
  };

  const hasActiveSounds = Object.keys(activeAmbientSounds).length > 0;

  if (loading) {
    return (
      <div className="player-card p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="player-card p-6" data-testid="ambient-mixer">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold text-white">
          Sons Ambiants
        </h3>
        <div className="flex items-center gap-2">
          {/* Timer button */}
          <button
            onClick={() => setShowTimerPanel(!showTimerPanel)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${timerActive ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/5 text-white/50 border border-white/10 hover:text-white hover:bg-white/10'}`}
            data-testid="timer-toggle-btn"
          >
            <Moon className="w-3.5 h-3.5" />
            {timerActive ? formatRemaining(timerRemaining) : 'Sommeil / Sieste'}
          </button>
          {hasActiveSounds && (
            <button
              onClick={stopAllAmbient}
              className="text-sm text-white/60 hover:text-pink-400 transition-colors"
              data-testid="stop-all-ambient-btn"
            >
              Tout arrêter
            </button>
          )}
          <Button
            onClick={() => setShowUpload(!showUpload)}
            variant="outline"
            size="sm"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="add-ambient-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Active Timer Display */}
      {timerActive && (
        <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-between" data-testid="timer-active-display">
          <div className="flex items-center gap-2.5">
            {timerMode === 'alarm' ? <AlarmClock className="w-4 h-4 text-amber-400" /> : <Timer className="w-4 h-4 text-amber-400" />}
            <div>
              <p className="text-white text-sm font-medium">{formatRemaining(timerRemaining)}</p>
              <p className="text-amber-400/60 text-xs">{timerMode === 'alarm' ? 'Réveil programmé' : 'Arrêt automatique'}</p>
            </div>
          </div>
          <button onClick={cancelTimer} className="p-1.5 rounded-full text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all" data-testid="timer-cancel-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Timer / Sleep Panel */}
      {showTimerPanel && (
        <div className="mb-4 p-4 rounded-xl bg-white/5 border border-white/10 space-y-4" data-testid="timer-panel">
          <div className="flex items-center justify-between">
            <p className="text-white font-medium text-sm flex items-center gap-2"><Moon className="w-4 h-4 text-amber-400" /> Minuterie & Sommeil</p>
            <button onClick={() => setShowTimerPanel(false)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="flex items-center justify-between gap-2">
            <p className="text-white/50 text-xs">Pendant la minuterie / le réveil :</p>
            <KeepAwakeToggle keepAwake={keepAwake} onToggle={toggleKeepAwake} testId="keep-awake-sieste"
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/10 border border-white/15 text-white/80 hover:bg-white/20" />
          </div>

          {/* Quick durations */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Sieste / Durée</p>
            <div className="grid grid-cols-4 gap-2">
              {[15, 30, 45, 60].map(min => (
                <button key={min} onClick={() => startDurationTimer(min)}
                  className="py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-amber-500/15 hover:border-amber-500/25 transition-all active:scale-95"
                  data-testid={`timer-${min}min`}
                >
                  {min}min
                </button>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {[90, 120, 180, 480].map(min => (
                <button key={min} onClick={() => startDurationTimer(min)}
                  className="py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-amber-500/15 hover:border-amber-500/25 transition-all active:scale-95"
                  data-testid={`timer-${min}min`}
                >
                  {min >= 60 ? `${min / 60}h` : `${min}min`}
                </button>
              ))}
            </div>
            {/* Custom duration */}
            <div className="flex items-center gap-2 mt-3">
              <Timer className="w-4 h-4 text-white/40" />
              <input
                type="number" min="1" max="720" value={timerDuration}
                onChange={(e) => setTimerDuration(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm text-center"
                data-testid="timer-custom-input"
              />
              <span className="text-white/40 text-xs">min</span>
              <button onClick={() => startDurationTimer(timerDuration)}
                className="flex-1 py-2 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-400 text-sm font-medium hover:bg-amber-500/30 transition-all active:scale-95"
                data-testid="timer-custom-start"
              >
                Lancer
              </button>
            </div>
          </div>

          {/* Alarm / Wake up */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Réveil</p>
            <div className="flex items-center gap-2">
              <AlarmClock className="w-4 h-4 text-white/40" />
              <input
                type="time" value={alarmTime}
                onChange={(e) => setAlarmTime(e.target.value)}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm [color-scheme:dark]"
                data-testid="timer-alarm-input"
              />
              <button onClick={startAlarmTimer}
                className="px-4 py-2 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-400 text-sm font-medium hover:bg-amber-500/30 transition-all active:scale-95"
                data-testid="timer-alarm-start"
              >
                Programmer
              </button>
            </div>
          </div>

          {/* Alarm Sound Selection */}
          <div>
            <button onClick={() => setShowAlarmSounds(!showAlarmSounds)}
              className="w-full flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-amber-500/20 transition-all"
              data-testid="alarm-sound-toggle"
            >
              <span className="text-white/60 text-xs flex items-center gap-2">
                <Volume2 className="w-3.5 h-3.5" /> Son de réveil
              </span>
              <span className="text-amber-400 text-xs">{selectedAlarm.split('/').pop().replace(/\.\w+$/, '').replace(/_/g, ' ')}</span>
            </button>

            {showAlarmSounds && (
              <div className="mt-2 p-3 rounded-xl bg-black/40 border border-white/10 space-y-3 max-h-64 overflow-y-auto" data-testid="alarm-sounds-list">
                {ALARM_CATEGORIES.map(cat => (
                  <div key={cat.id}>
                    <p className="text-amber-400/70 text-[10px] uppercase tracking-wider mb-1.5 font-medium">{cat.label}</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {cat.sounds.map(s => (
                        <button key={s.url} onClick={() => selectAlarmSound(s.url)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs transition-all text-left ${selectedAlarm === s.url ? 'bg-amber-500/20 border border-amber-500/40 text-amber-400' : 'bg-white/5 border border-white/5 text-white/60 hover:bg-white/10'}`}
                          data-testid={`alarm-${cat.id}-${s.name.toLowerCase()}`}
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Custom uploaded alarms */}
                {customAlarms.length > 0 && (
                  <div>
                    <p className="text-amber-400/70 text-[10px] uppercase tracking-wider mb-1.5 font-medium">Mes sons</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {customAlarms.map((a, i) => (
                        <button key={a.url || `alarm-${i}`} onClick={() => selectAlarmSound(a.url)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs transition-all text-left truncate ${selectedAlarm === a.url ? 'bg-amber-500/20 border border-amber-500/40 text-amber-400' : 'bg-white/5 border border-white/5 text-white/60 hover:bg-white/10'}`}
                        >
                          {a.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload own alarm */}
                <div>
                  <input ref={alarmFileRef} type="file" accept="audio/*" onChange={handleAlarmUpload} className="hidden" />
                  <button onClick={() => alarmFileRef.current?.click()}
                    className="w-full py-2 rounded-lg border border-dashed border-white/15 text-white/40 text-xs hover:border-amber-500/30 hover:text-amber-400/60 transition-all flex items-center justify-center gap-1.5"
                    data-testid="upload-alarm-sound"
                  >
                    <Upload className="w-3 h-3" /> Importer mon propre son
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Unlimited */}
          <div className="pt-2 border-t border-white/5 text-center">
            <p className="text-zinc-500 text-xs">Sans minuterie, les sons jouent en continu sans limite</p>
          </div>
        </div>
      )}

      {/* Upload Form */}
      {showUpload && (
        <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/10 space-y-3">
          <p className="text-sm text-white/70 font-medium">Importer un son ambiant</p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="ambient-file-input"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full p-3 border-2 border-dashed border-white/20 rounded-lg text-center hover:border-amber-400/50 transition-colors"
            data-testid="ambient-file-zone"
          >
            {selectedFile ? (
              <span className="text-amber-400">{selectedFile.name}</span>
            ) : (
              <span className="text-white/50">
                <Upload className="w-5 h-5 mx-auto mb-1" />
                Cliquez pour choisir un fichier audio
              </span>
            )}
          </button>

          <Input
            placeholder="Nom du son *"
            value={uploadForm.name}
            onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
            className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
            data-testid="ambient-name-input"
          />

          {/* Icon Selector */}
          <div>
            <p className="text-xs text-white/50 mb-2">Choisir une icône:</p>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map(({ value, label, Icon }) => (
                <button
                  key={value}
                  onClick={() => setUploadForm(prev => ({ ...prev, icon: value }))}
                  className={`p-2 rounded-lg border transition-all ${
                    uploadForm.icon === value
                      ? 'border-amber-400 bg-amber-400/20'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                  title={label}
                >
                  <Icon className={`w-4 h-4 ${uploadForm.icon === value ? 'text-amber-400' : 'text-white/60'}`} />
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !uploadForm.name}
              className="flex-1 bg-amber-600 hover:bg-amber-500"
              data-testid="upload-ambient-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {uploading ? 'Upload...' : 'Ajouter le son'}
            </Button>
            <Button
              onClick={() => {
                setShowUpload(false);
                setSelectedFile(null);
                setUploadForm({ name: '', icon: 'music' });
              }}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Sound Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
        {ambientSounds.length === 0 ? (
          <div className="col-span-full text-center py-6 text-white/50">
            <Music className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Aucun son ambiant</p>
            <p className="text-sm">Cliquez sur "Ajouter" pour importer vos sons</p>
          </div>
        ) : (
          ambientSounds.map((sound) => {
            const Icon = getIcon(sound.icon);
            const isActive = !!activeAmbientSounds[sound.id];
            
            return (
              <div key={sound.id} className="relative group">
                <button
                  onClick={() => toggleAmbientSound(sound)}
                  className={`sound-btn w-full ${isActive ? 'active' : ''}`}
                  data-testid={`ambient-sound-${sound.id}`}
                >
                  <Icon className={`w-6 h-6 ${isActive ? 'text-amber-400' : 'text-white/60'}`} />
                  <span className={`text-xs ${isActive ? 'text-white' : 'text-white/60'} truncate max-w-full`}>
                    {sound.name.split(' ')[0]}
                  </span>
                </button>
                {/* Delete button - hidden for locked sounds */}
                {!sound.locked && (
                  <button
                    onClick={(e) => handleDelete(sound, e)}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-500"
                    data-testid={`delete-ambient-${sound.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Active Sound Volume Controls */}
      {hasActiveSounds && (
        <div className="space-y-4 mb-6">
          <h4 className="text-sm text-white/60 font-medium">Volumes individuels</h4>
          {Object.values(activeAmbientSounds).map((sound) => {
            const Icon = getIcon(sound.icon);
            const volume = ambientVolumes[sound.id] || 0.5;
            
            return (
              <div key={sound.id} className="flex items-center gap-4">
                <Icon className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span className="text-sm text-white/80 w-24 truncate">{sound.name}</span>
                <Slider
                  value={[volume * 100]}
                  onValueChange={(value) => updateAmbientVolume(sound.id, value[0] / 100)}
                  max={100}
                  step={1}
                  className="flex-1"
                  data-testid={`ambient-volume-${sound.id}`}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Master Volume */}
      <div className="pt-4 border-t border-white/10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => updateMasterVolume(masterVolume === 0 ? 0.8 : 0)}
            className="text-white/60 hover:text-white transition-colors"
            data-testid="master-mute-btn"
          >
            {masterVolume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <span className="text-sm text-white/60 w-24">Volume Master</span>
          <Slider
            value={[masterVolume * 100]}
            onValueChange={(value) => updateMasterVolume(value[0] / 100)}
            max={100}
            step={1}
            className="flex-1"
            data-testid="master-volume-slider"
          />
        </div>
      </div>
    </div>
  );
};

export default AmbientMixer;
